import {useContext, useEffect} from 'react';
import {MessageDisplayerUtility} from "../MessageDisplayerUtility";
import {LeaveWithoutSavingWarningUtility} from "../LeaveWithoutSavingWarningUtility";
import {GlobalContext} from "../../admin-frame/AdminFrame";
import {useRenderableData} from "./useRenderableData";
import {GenericModel} from "./GenericModel";

// Engine for a wrapper component for a form, where wrapper is in charge of updating models defined by the form. Updating includes "deleting" which is really just deactivating.
export function useModelUpdateEngine(
    modelFetchApiFunction: Function,
    modelUpdateApiFunction: Function,
    modelId: number | string,
    modelName: string,
    updateCallback: Function
) {
    const context = useContext(GlobalContext);
    const updateEngineRenderableData = useRenderableData({loading: true});

    // on mount, fetch model from API and put it in state
    useEffect(function () {
        updateEngineRenderableData.updateAndRender({loading: true})
        modelFetchApiFunction(modelId)
            .then(function (model: GenericModel) {
                updateEngineRenderableData.updateAndRender({
                    model: model,
                    loading: false,
                });
            })
            .catch(function (error: object) {
                updateEngineRenderableData.updateAndRender({loading: false});
                console.log(`Error fetching ${modelName}: `, error);
                MessageDisplayerUtility.error(`Error fetching ${modelName}.`);
            });
    }, [modelId]);

    function updateModel(formModelData: object) {
        updateEngineRenderableData.updateAndRender({loading: true})
        modelUpdateApiFunction(formModelData)
            .then(() => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully updated ${modelName}.`);
                    if (updateCallback) {
                        updateCallback();
                    }
                }, 0)
            })
            .catch((error: object) => {
                updateEngineRenderableData.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while updating the ${modelName}.`);
            });
    }

    // "deletes" are just updates where active is changed to false
    function deactivateModel() {
        updateEngineRenderableData.updateAndRender({loading: true})
        modelUpdateApiFunction({id: modelId, active: false})
            .then(() => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully deleted ${modelName}.`);
                    if (updateCallback) {
                        updateCallback();
                    }
                }, 0)
            })
            .catch((error: object) => {
                updateEngineRenderableData.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while deleting the ${modelName}.`);
            });
    }

    function showConfirmDeactivateModal() {
        updateEngineRenderableData.updateAndRender({showConfirmDeactivateModal: true});
    }

    function hideConfirmDeactivateModal() {
        updateEngineRenderableData.updateAndRender({showConfirmDeactivateModal: false});
    }

    return {
        renderableData: updateEngineRenderableData,
        updateModel: updateModel,
        deactivateModel: deactivateModel,
        showConfirmDeactivateModal: showConfirmDeactivateModal,
        hideConfirmDeactivateModal: hideConfirmDeactivateModal,
    };
}
